import type {IApiErrorMetadata} from '@stripe-internal/connect-embedded-lib';
import {
  handleFetchResponse,
  getFormURLEncodedBody,
  getStripeOrGraphQLErrorMetadata,
} from '@stripe-internal/connect-embedded-lib';
import type {StripeAccountFragmentData} from '@stripe-internal/connect-embedded-lib/dist/ExternalAccounts/queries/stripeAccountFragment';
import {getDevLogger} from '../utils/getLogger';
import type {RiskSignalsCollectionDataLayerResponse} from './types';
import type {
  IFrameCollectRiskSignalsRequest,
  IFrameCollectRiskSignalsResponse,
} from '../data-layer-client/FrameMessenger';
import type {RiskSignalCollectionErrorType} from '../connect/ConnectJSInterface/RiskSignalCollectionTypes';
import {authStore} from './authentication/AuthenticationStore';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import {getStripeApiHost} from '../data-layer-client/buildStripeClient';
import {getDataLayerHeaders} from './proxyRequest';
import {getClaimInfo} from './DataLayer';

const devLogger = getDevLogger();

const RISK_SIGNALS_REQUIREMENT_NAME = 'risk_signals.session_metadata';

/**
 * Collect and send risk signals to Stripe via the /v1/risk_signals endpoint
 * @param request {IFrameCollectRiskSignalsRequest} The data layer request
 * @returns {IFrameCollectRiskSignalsResponse} The data layer response
 */
export async function collectAndSendRiskSignals(
  request: IFrameCollectRiskSignalsRequest,
): Promise<IFrameCollectRiskSignalsResponse> {
  devLogger.info('Data Layer: collectAndSendRiskSignals called');

  try {
    let response: RiskSignalsCollectionDataLayerResponse;

    // Check if the account has risk signals requirement
    const hasRiskSignalsRequirement =
      await doesAccountHaveRiskSignalsRequirement();
    if (!hasRiskSignalsRequirement) {
      devLogger.info('Account does not have risk signals requirement');
      response = {
        status: 'skipped',
        skippedReason: 'requirement_fulfilled',
      };
    } else {
      devLogger.info(
        'Account has risk signals requirement, sending risk signals to Stripe',
      );
      // Send risk signals to Stripe
      await sendRiskSignalsToStripe();

      response = {
        status: 'completed',
      };
    }

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response,
    };
  } catch (error) {
    devLogger.error('Error in collectAndSendRiskSignals:', error);

    // Map error to the correct RiskSignalCollectionErrorType
    const mappedError = mapToRiskSignalCollectionError(error);

    return {
      id: request.id,
      type: request.type,
      requestType: 'collectRiskSignalsResponse',
      response: {
        status: 'failed',
        error: mappedError,
      },
    };
  }
}

/**
 * Check if the account has risk signals requirement
 * @returns True if the account risk_signals.session_metadata requirement exists, false otherwise
 */
async function doesAccountHaveRiskSignalsRequirement(): Promise<boolean> {
  const accountId = getClaimInfo()?.connectedAccountId;
  if (!accountId) {
    // this should never happen as risk signal collection is only called after authentication success which sets the claim info
    throw new Error('No valid connectedAccountId found in claimInfo');
  }

  const apiUrl = buildApiUrl(`/v1/accounts/${accountId}`);
  const fetchResponse = await fetch(apiUrl, {
    method: 'GET',
    headers: buildApiRequestHeaders(),
  });
  const accountData: StripeAccountFragmentData = await handleFetchResponse(
    fetchResponse,
  );

  // Check if risk_signals.session_metadata requirement exists
  return (
    accountData.requirements?.currently_due?.includes(
      RISK_SIGNALS_REQUIREMENT_NAME,
    ) || false
  );
}

/**
 * Send risk signals to Stripe via the /v1/risk_signals endpoint
 * @returns The risk signals response from Stripe
 */
async function sendRiskSignalsToStripe() {
  const apiUrl = buildApiUrl('/v1/risk_signals');

  // Make request to /v1/accounts endpoint
  devLogger.info('Posting risk signals to /v1/risk_signals', {
    apiUrl,
  });

  const fetchResponse = await fetch(apiUrl, {
    method: 'POST',
    headers: buildApiRequestHeaders(),
    body: getFormURLEncodedBody({
      // TODO (@justinrao: 2025-12-31) add hcaptcha and iovation data
    }),
  });

  return handleFetchResponse(fetchResponse);
}

function buildApiRequestHeaders(): HeadersInit {
  // Get API key
  const apiKey = authStore.getApiKey('account_session');
  if (!apiKey) {
    throw new Error('No valid account session apiKey found');
  }

  // Build headers
  return getDataLayerHeaders({
    Authorization: `Bearer ${apiKey.key}`,
    'Content-Type': 'application/x-www-form-urlencoded',
  });
}

function buildApiUrl(path: string): string {
  // Get API host
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const apiHost = getStripeApiHost(serviceEnvironment);

  return `https://${apiHost}/${path}`;
}

/**
 * Maps errors to RiskSignalCollectionError (excluding connect.js loading errors)
 */
function mapToRiskSignalCollectionError(error: unknown): {
  type: RiskSignalCollectionErrorType;
  message?: string;
} {
  // follow the same pattern used in auth error mapping
  // with what's applicable to risk signal collection
  const e = getStripeOrGraphQLErrorMetadata(error);

  const getErrorType = (e: IApiErrorMetadata) => {
    if (e.stripeErrorCode === 'Failed to fetch') {
      return 'api_connection_error';
    }
    if (e.stripeStatusCode === 429) {
      return 'rate_limit_error';
    }
    if (e.stripeStatusCodeRange === '4xx') {
      return 'invalid_request_error';
    }
    // default to api_error
    return 'api_error';
  };

  const errorType = getErrorType(e);
  return {type: errorType, message: e.stripeErrorMessage};
}
